import checkBoxIcon from "assets/wealth/icons/check_box.svg";
import checkBoxCheckedIcon from "assets/wealth/icons/check_box_checked.svg";
import { areAbsolutelyEqual } from "helpers/commonFunctions";
import ErrorMessage from "components/form/ErrorMessage";
import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import NumberField from "../../../NumberField";
import SelectField from "../../../SelectField";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import useDevice from "hooks/useDevice";

const TableContentMobile = ({
  listAnswer,
  control,
  field,
  error,
  name,
  disabled,
}) => {
  const { isMobile } = useDevice();
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  const tableRows = useMemo(() => {
    return listAnswer?.filter((i) => i.subType === "ROW_QUESTION");
  }, [listAnswer]);

  const handleChangeCheckbox = (answerId) => (event) => {
    const value = field.value || [];
    const index = value.findIndex((item) => item.answerId === answerId);
    if (index >= 0) {
      value.splice(index, 1);
    }
    if (event.target.checked) {
      value.push({ answerId });
    }
    field.onChange([...value]);
  };
  const handleChangeTextbox = (answerId) => (event) => {
    const value = field.value || [];
    const index = value.findIndex((item) => item.answerId === answerId);
    if (index >= 0) {
      value.splice(index, 1);
    }
    if (event.target.value) {
      value.push({ answerId, text: event.target.value });
    }
    field.onChange([...value]);
  };
  const checkIndex = (answerId) => {
    const value = field.value || [];
    return value.findIndex((item) => item.answerId === answerId);
  };
  const handleChangeNumberField = (answerId, numberValue) => {
    const value = field.value || [];
    const index = value.findIndex((item) => item.answerId === answerId);
    if (index >= 0) {
      value.splice(index, 1);
    }
    if (numberValue || numberValue === 0) {
      value.push({ answerId, text: numberValue });
    }
    field.onChange([...value]);
  };

  const renderAnswerByType = (r) => {
    let err;
    if (Array.isArray(error)) {
      err = error?.find((i) => {
        return i?.text?.message?.answerId === r?.elementId;
      });
    }
    switch (r.subType) {
      case "CHOICE_ANSWER":
        return (
          <FormControlLabel
            ref={field.ref}
            label={r.title}
            control={
              <Checkbox
                disabled={disabled}
                icon={<img style={{ width: 16 }} src={checkBoxIcon} alt="" />}
                checkedIcon={
                  <img style={{ width: 16 }} src={checkBoxCheckedIcon} alt="" />
                }
                onChange={handleChangeCheckbox(r.elementId)}
                checked={
                  checkIndex(r.elementId) >= 0 ||
                  areAbsolutelyEqual(r?.answerText, r?.title)
                }
              />
            }
            sx={{
              "& .MuiFormControlLabel-label": {
                color: "#52575C",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: 1.4,
                letterSpacing: 0,
                flex: "1 1",
                wordWrap: "break-word",
                maxWidth: isMobile ? "calc(100% - 2rem)" : "unset",
              },
              "& .MuiFormControlLabel-label.Mui-disabled": {
                color: "#52575C",
              },
              "& .MuiCheckbox-root": {
                padding: "4px",
                marginRight: "8px",
                flexShrink: 0,
              },
              margin: 0,
              marginBottom: "16px",
              minWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
              maxWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
            }}
          />
        );
      case "CELL_TITLE":
        return (
          <Typography
            sx={{
              color: "#52575C",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: 1.4,
              letterSpacing: 0,
              wordWrap: "break-word",
              margin: 0,
              marginBottom: "16px",
              minWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
              maxWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
            }}
          >
            {r.title}
          </Typography>
        );
      case "TEXT_ANSWER":
        return (
          <TextField
            onChange={handleChangeTextbox(r.elementId)}
            variant="outlined"
            size="small"
            label={r.title}
            disabled={disabled}
            value={r.answerText ?? null}
            sx={{
              "& .MuiInputBase-input": { height: "27px" },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#000000de", // màu chữ
              },
              margin: 0,
              marginBottom: "16px",
              minWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
              maxWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
            }}
          />
        );
      case "NUMBER_ANSWER":
        return (
          <NumberField
            onChange={(e) => {
              handleChangeNumberField(r.elementId, e?.target?.value ?? null);
            }}
            error={err}
            label={r.title}
            disabled={disabled}
            value={
              r.answerText
                ? Number(r.answerText)
                : checkIndex(r.elementId) >= 0
                ? field.value.find((item) => item.answerId === r.elementId)
                    ?.text
                : null
            }
            variant="outlined"
            size="small"
            min={r.min}
            max={r.max}
            thousandSeparator
            isNumericString
            textFieldSx={{
              "& .MuiInputBase-input": { height: "27px" },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#000000de", // màu chữ
              },
              margin: 0,
              marginBottom: "16px",
              minWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
              maxWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
            }}
          />
        );
      case "DROP_LIST_ANSWER":
        return (
          <SelectField
            onChange={handleChangeTextbox(r.elementId)}
            uuidRef={r.uuidRef}
            disabled={disabled}
            answer={r}
            label={r.title}
            fieldValue={
              field?.value
                ? field?.value?.find((i) => i.answerId === r.elementId)
                : null
            }
            sx={{
              "& .MuiSelect-outlined": {
                padding: "2px 14px",
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#000000de", // màu chữ
              },
              height: "44px",
              margin: 0,
              minWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
              maxWidth: isMobile ? "100%" : "calc((100% - 16px) / 3)",
            }}
          />
        );
      default:
        return <></>;
    }
  };
  return (
    <Box
      sx={{
        width: "100%",
        margin: "16px 0",
        background: "white",
        borderLeft: "1px solid #00B74F",
      }}
    >
      {tableRows?.map((row, idx) => {
        return (
          <Accordion
            expanded={expanded === row.elementId}
            onChange={handleChange(row.elementId)}
            sx={{
              boxShadow: "none",
              border: "none",
              "&:before": {
                display: "none",
              },
              "&.Mui-expanded": {
                margin: 0,
              },
            }}
          >
            <AccordionSummary
              sx={{
                borderBottom: "none",
                margin: 0,
                "&.Mui-expanded": {
                  minHeight: "unset",
                },
              }}
              expandIcon={
                expanded === row.elementId ? (
                  <RemoveCircleIcon
                    sx={{
                      color: "#00AD53",
                      fontSize: "13px",
                      marginTop: "1px",
                    }}
                  />
                ) : (
                  <AddCircleIcon
                    sx={{
                      color: "#0B263D",
                      fontSize: "13px",
                      marginTop: "1px",
                    }}
                  />
                )
              }
              aria-controls={`panel${row.elementId}-content`}
              id={`panel${row.elementId}-header`}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: 1.3,
                  letterSpacing: 0,
                }}
              >
                {row.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {row.children.map((r, index) => {
                return <>{renderAnswerByType(r)}</>;
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
      {error?.message && <ErrorMessage message={error?.message} />}
    </Box>
  );
};

export default TableContentMobile;
